import { useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { Checkbox } from "~/components/ui/checkbox";
import { FormField } from "~/components/ui/form-field";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { CountrySelect } from "~/components/country-select";
import { LanguageSelect } from "~/components/language-select";
import { CustomMultiSelect } from "~/components/ui/custom-multi-select";

import { cn } from "~/lib/cn";
import { authMiddleware } from "~/lib/auth-middleware";
import { createServerFn } from "@tanstack/react-start";
import {
  getUserPreferences,
  updateUserPreferences,
} from "~/lib/functions/user";

import {
  CoachDiscoverySchema,
  coachAgesField,
  coachGenderField,
  coachLocationCountryField,
  coachLocationRegionField,
  coachPersonalityField,
  coachingApproachesField,
  coachingFormatsFields,
  coachingPackagesField,
  faithIntegrationsField,
  goalsField,
  otherFaithIntegrationField,
  otherGoalField,
  personalContextField,
  prefersCoachLocationField,
  sessionDeliveryMethodsFeild,
  startTimeField,
  supportField,
  userPersonalityField,
} from "~/lib/schemas/coach-discovery";

import type {
  CoachDiscoveryForm,
  FaithIntegrationOptions,
  GoalOptions,
} from "~/lib/schemas/coach-discovery";

import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";
import { z } from "zod";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const getFormData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const userPreferences = await getUserPreferences({
      data: { userId: context.user.id },
    });
    return transformUserPreferencesToDiscoveryForm(userPreferences);
  });

export const updateFormData = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ data: CoachDiscoverySchema }))
  .handler(async ({ data, context }) => {
    const values = data.data;
    const embeddingPrompt = `
    This user is seeking a coach with the following preferences:
    
    Primary Coaching Needs:
    - Main goals: ${values.goals.join(", ")}
    - Other goals: ${values.otherGoal || "Not specified"}

    Coaching Format & Delivery:
    - Coaching Format: ${values.coachingFormats}
    - Session Format: ${values.sessionDeliveryMethods}

    Experience Level & Coaching Packages:
    - Coach Experience Levels: ${values.coachingPackages.join(", ")}

    Coaching Style & Approach
    - Coaching Approaches: ${values.coachingApproaches.join(", ")}

    Coach Characteristics Preferences:
    - Preferred coach gender: ${values.coachGender || "No preference"}
    - Faith/religion alignment: ${values.faithIntegration.join(", ")}
    - Language requirements: ${values.preferredLanguages?.join(", ")}
    - Coach Ages: ${values.coachAges.join(", ")}
    
    Location & Logistics:
    - Prefers coach location: ${values.prefersCoachLocation}
    - Coach location region: ${values.coachLocationRegion || "Not specified"}
    - Coach location country: ${values.coachLocationCountry || "Not specified"}
    
    Additional Context:
    - Other faith preference: ${values.otherFaithIntegration || "Not specified"}
    `;

    try {
      const userId = context.user.id;

      const { serverEnv } = await import("~/lib/server-env");
      const { getAIPreferencesGenerator } = await import(
        "~/lib/ai-preferences-generator"
      );

      const AiPreferencesGenerator = getAIPreferencesGenerator(
        serverEnv.EMBEDDINGS_OPENAI_API_KEY,
        serverEnv.EMBEDDINGS_OPENAI_BASE_URL!,
      );

      const embedding =
        await AiPreferencesGenerator.createEmbedding(embeddingPrompt);

      if (userId) {
        await updateUserPreferences({
          data: {
            userId,
            preferences: {
              ...values,
              preferencesEmbedding: embedding,
            },
          },
        });
      }
      return {
        status: "success",
        message: "Preferences updated",
      };
    } catch (error) {
      console.error("Failed to save user preferences:", error);
      throw new Error("Failed to save user preferences");
    }
  });

export const Route = createFileRoute(
  "/_user/settings/profile/coaching-preferences",
)({
  head: () =>
    createNoIndexMeta(
      "Coaching Preferences – Dream Coach Match",
      "Manage your coaching preferences and requirements",
    ),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["user-preferences"],
      queryFn: () => getFormData(),
    });
    return {};
  },
  component: CoachingPreferencesPage,
});

function CoachingPreferencesPage() {
  const { data: initialData } = useQuery({
    queryKey: ["user-preferences"],
    queryFn: () => getFormData(),
  });

  const queryClient = useQueryClient();

  const { register, handleSubmit, watch, setValue, getValues, formState } =
    useForm<CoachDiscoveryForm>({
      resolver: zodResolver(CoachDiscoverySchema),
      defaultValues: initialData || {},
    });

  const { errors, isSubmitting } = formState;

  const updateMutation = useMutation({
    mutationFn: (data: CoachDiscoveryForm) =>
      updateFormData({ data: { data } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
    },
    onError: (_error: Error) => {
      // TODO: Show user-friendly error message
    },
  });

  const onSubmit = (data: CoachDiscoveryForm) => {
    updateMutation.mutate(data);
  };

  const watchedGoals = watch("goals");
  const watchedFaithIntegration = watch("faithIntegration");
  const watchedPrefersCoachLocation = watch("prefersCoachLocation");
  const watchedUserPersonality = watch("userPersonality");
  const watchedCoachPersonality = watch("coachPersonality");

  // Memoized handler for userPersonality changes to prevent array recreation on every render
  const handleUserPersonalityChange = useCallback(
    (value: string, checked: boolean) => {
      const currentValue = getValues("userPersonality") || [];

      if (checked) {
        if ("Skip this question" === value) {
          setValue("userPersonality", [value]);
        } else {
          const filtered = currentValue.filter(
            (v) => "Skip this question" !== v,
          );
          setValue("userPersonality", [...filtered, value]);
        }
      } else {
        setValue(
          "userPersonality",
          currentValue.filter((v) => v !== value),
        );
      }
    },
    [getValues, setValue],
  );

  const validateOtherGoal = (value: string | null | undefined) => {
    const goals = getValues("goals") as string[] | undefined;
    if (goals?.includes("Other") && !value) {
      return "Please describe your goal in the text area if you selected 'Other'.";
    }
    return true;
  };

  const validateOtherFaith = (value: string | undefined) => {
    const preference = getValues("faithIntegration") as string[];
    if (preference?.includes("Other") && !value) {
      return "Please describe your faith in the text area if you selected 'Other'.";
    }
    return true;
  };

  const _validateCountry = (value: string | undefined) => {
    const preference = getValues("prefersCoachLocation") as string;
    if ("Yes" === preference && !value) {
      return "Please select your country if you selected 'Yes'.";
    }
    return true;
  };

  return (
    <>
      <Card className="mb-8 flex space-x-8 p-8 shadow-none hover:shadow-none">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-8">
          <div className="absolute right-6 top-0">
            <Button
              size="sm"
              type="submit"
              disabled={isSubmitting || updateMutation.isPending}
            >
              {isSubmitting || updateMutation.isPending
                ? "Saving..."
                : "Save changes"}
            </Button>
          </div>

          {/* STEP 1 */}
          <div className="mt-4">
            <FormField label="Goals" error={errors.goals?.message}>
              {goalsField.options?.map(({ value, label }) => (
                <div key={value} className="flex items-center">
                  <Checkbox
                    {...register("goals")}
                    value={value}
                    checked={watchedGoals?.includes(value as GoalOptions)}
                    onChange={(e) => {
                      const checked = (e.target as HTMLInputElement).checked;
                      const currentValue = getValues("goals") || [];

                      if (checked) {
                        setValue("goals", [
                          ...currentValue,
                          value as GoalOptions,
                        ]);
                      } else {
                        setValue(
                          "goals",
                          currentValue.filter((v) => v !== value),
                        );
                      }
                    }}
                  >
                    {label}
                  </Checkbox>
                </div>
              ))}
            </FormField>

            {/* Other Goal */}
            <div
              className={cn(
                "max-h-0 overflow-hidden px-1 opacity-0 transition-all duration-700",
                {
                  "max-h-fit opacity-100": watchedGoals?.includes("Other"),
                },
              )}
            >
              <FormField error={errors.otherGoal?.message}>
                <Textarea
                  {...register("otherGoal", { validate: validateOtherGoal })}
                  className="resize-none"
                  maxLength={otherGoalField.maxLength}
                />
              </FormField>
            </div>
          </div>

          {/* STEP 2 */}
          <FormField
            labelSize="sm"
            error={errors.startTime?.message}
            label={startTimeField.subTitle || ""}
          >
            <RadioGroup className="mt-6 flex flex-col gap-y-6">
              {startTimeField.options?.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    {...register("startTime")}
                    value={value}
                    id={`startTime-${value}`}
                  />
                  <label
                    htmlFor={`startTime-${value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {label}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </FormField>

          <FormField
            labelSize="sm"
            error={errors.support?.message}
            label={supportField.subTitle || ""}
          >
            <RadioGroup className="mt-6 flex flex-col gap-y-6">
              {supportField.options?.map(({ value, label, description }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    {...register("support")}
                    value={value}
                    id={`support-${value}`}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor={`support-${value}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {label}
                    </label>
                    {description && (
                      <p className="text-sm text-muted-foreground">
                        {description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </RadioGroup>
          </FormField>

          {/* STEP 3 */}
          <FormField
            label={coachingFormatsFields.subTitle || ""}
            error={errors.coachingFormats?.message}
          >
            <RadioGroup>
              {coachingFormatsFields.options?.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    {...register("coachingFormats")}
                    value={value}
                    id={`coachingFormats-${value}`}
                  />
                  <label
                    htmlFor={`coachingFormats-${value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {label}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </FormField>

          <FormField
            label={"Coaching Style"}
            error={errors.coachingApproaches?.message}
          >
            <CustomMultiSelect
              field={{
                name: "coachingApproaches",
                value: watch("coachingApproaches") || [],
              }}
              props={register("coachingApproaches")}
              placeholder=""
              variant="slim"
              exclusiveValues={["Not sure yet"]}
              items={coachingApproachesField.options || []}
            />
          </FormField>

          {/* STEP 4 */}
          <FormField
            label="Coaching experience levels"
            error={errors.coachingPackages?.message}
          >
            <CustomMultiSelect
              field={{
                name: "coachingPackages",
                value: watch("coachingPackages") || [],
              }}
              props={register("coachingPackages")}
              placeholder=""
              variant="slim"
              items={coachingPackagesField.options || []}
              exclusiveValues={["No preference"]}
            />
          </FormField>

          {/* STEP 5 */}
          <FormField label="Coach ages" error={errors.coachAges?.message}>
            <CustomMultiSelect
              field={{
                name: "coachAges",
                value: watch("coachAges") || [],
              }}
              props={register("coachAges")}
              placeholder=""
              variant="slim"
              items={coachAgesField.options || []}
              exclusiveValues={["No preference"]}
            />
          </FormField>

          <FormField label="Coach gender" error={errors.coachGender?.message}>
            <RadioGroup>
              {coachGenderField.options?.map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    {...register("coachGender")}
                    value={value}
                    id={`coachGender-${value}`}
                  />
                  <label
                    htmlFor={`coachGender-${value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {label}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </FormField>

          {/* STEP 6 */}
          <div className="mt-8">
            <FormField
              labelSize="sm"
              error={errors.userPersonality?.message}
              label="User personality traits"
            >
              <div className="mt-4 flex flex-col gap-y-6">
                {userPersonalityField.options?.map(
                  ({ value, label, description }) => (
                    <div key={value} className="flex items-start gap-2">
                      <Checkbox
                        {...register("userPersonality")}
                        value={value}
                        id={`userPersonality-${value}`}
                        checked={watchedUserPersonality?.includes(value)}
                        onChange={(e) => {
                          const checked = (e.target as HTMLInputElement)
                            .checked;
                          handleUserPersonalityChange(value, checked);
                        }}
                      />
                      <div className="grid w-full gap-1.5 leading-none">
                        <label
                          htmlFor={`userPersonality-${value}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {label}
                        </label>
                        {description && (
                          <p className="text-sm text-gray-500">{description}</p>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </FormField>
          </div>

          <div className="mt-8">
            <FormField
              labelSize="sm"
              error={errors.coachPersonality?.message}
              label="Preferred coach personality traits"
            >
              <div className="mt-4 flex flex-col gap-y-6">
                {coachPersonalityField.options?.map(
                  ({ value, label, description }) => (
                    <div key={value} className="flex items-start gap-2">
                      <Checkbox
                        {...register("coachPersonality")}
                        value={value}
                        id={`coachPersonality-${value}`}
                        checked={watchedCoachPersonality?.includes(value)}
                        onChange={(e) => {
                          const checked = (e.target as HTMLInputElement)
                            .checked;
                          const currentValue =
                            getValues("coachPersonality") || [];

                          if (checked) {
                            if ("Skip this question" === value) {
                              setValue("coachPersonality", [value]);
                            } else {
                              const filtered = currentValue.filter(
                                (v) => "Skip this question" !== v,
                              );
                              setValue("coachPersonality", [
                                ...filtered,
                                value,
                              ]);
                            }
                          } else {
                            setValue(
                              "coachPersonality",
                              currentValue.filter((v) => v !== value),
                            );
                          }
                        }}
                      />
                      <div className="grid w-full gap-1.5 leading-none">
                        <label
                          htmlFor={`coachPersonality-${value}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {label}
                        </label>
                        {description && (
                          <p className="text-sm text-gray-500">{description}</p>
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </FormField>
          </div>

          {/* STEP 7 */}
          <FormField
            label={sessionDeliveryMethodsFeild.subTitle || ""}
            error={errors.sessionDeliveryMethods?.message}
          >
            <RadioGroup>
              {sessionDeliveryMethodsFeild.options?.map(({ value, label }) => (
                <RadioGroupItem
                  key={value}
                  {...register("sessionDeliveryMethods")}
                  value={value}
                >
                  {label}
                </RadioGroupItem>
              ))}
            </RadioGroup>
          </FormField>

          <FormField
            label={prefersCoachLocationField.subTitle || ""}
            error={errors.prefersCoachLocation?.message}
          >
            <RadioGroup>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  key={prefersCoachLocationField?.options?.[0]?.value}
                  {...register("prefersCoachLocation")}
                  value={prefersCoachLocationField?.options?.[0]?.value}
                  id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                />
                <label
                  htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {prefersCoachLocationField?.options?.[0]?.label || ""}
                </label>
              </div>

              {/* Conditional Location Details */}
              <div
                className={cn(
                  "max-h-0 overflow-hidden opacity-0 transition-all duration-700",
                  {
                    "max-h-screen opacity-100":
                      watchedPrefersCoachLocation?.includes("Yes"),
                  },
                )}
              >
                <div className="mt-4 flex flex-col gap-4 px-1">
                  <FormField
                    label={coachLocationCountryField.subTitle}
                    error={errors.coachLocationCountry?.message}
                  >
                    <CountrySelect
                      value={watch("coachLocationCountry")}
                      onChange={(value) => {
                        setValue("coachLocationCountry", value, {
                          shouldValidate: true,
                        });
                      }}
                    />
                  </FormField>

                  <FormField
                    label={coachLocationRegionField.subTitle || ""}
                    error={errors.coachLocationRegion?.message}
                  >
                    <Input
                      {...register("coachLocationRegion")}
                      className="px-1"
                    />
                  </FormField>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  key={prefersCoachLocationField?.options?.[1]?.value}
                  {...register("prefersCoachLocation")}
                  value={prefersCoachLocationField?.options?.[1]?.value}
                  id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                />
                <label
                  htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {prefersCoachLocationField?.options?.[1]?.label ?? ""}
                </label>
              </div>
            </RadioGroup>
          </FormField>

          {/* STEP 8 */}
          <FormField
            label="Faith preferences"
            error={errors.faithIntegration?.message}
          >
            {faithIntegrationsField.options?.map(({ value, label }) => (
              <div key={value} className="flex items-center">
                <Checkbox
                  {...register("faithIntegration")}
                  value={value}
                  checked={watchedFaithIntegration?.includes(
                    value as FaithIntegrationOptions,
                  )}
                  onChange={(e) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    const currentValue = getValues("faithIntegration") || [];

                    if (checked) {
                      if ("No preference" === value) {
                        setValue("faithIntegration", ["No preference"]);
                        setValue("otherFaithIntegration", undefined);
                      } else {
                        setValue("faithIntegration", [
                          ...currentValue.filter((v) => "No preference" !== v),
                          value as FaithIntegrationOptions,
                        ]);
                      }
                    } else {
                      setValue(
                        "faithIntegration",
                        currentValue.filter((v) => v !== value),
                      );
                    }
                  }}
                >
                  {label}
                </Checkbox>
              </div>
            ))}
          </FormField>

          {/* Other Faith Textarea */}
          <div
            className={cn(
              "max-h-0 overflow-hidden px-1 opacity-0 transition-all duration-700",
              {
                "max-h-fit opacity-100":
                  watchedFaithIntegration?.includes("Other"),
              },
            )}
          >
            <FormField error={errors.otherFaithIntegration?.message}>
              <Textarea
                {...register("otherFaithIntegration", {
                  validate: validateOtherFaith,
                })}
                className="resize-none px-1"
                maxLength={otherFaithIntegrationField.maxLength}
                placeholder={otherFaithIntegrationField.placeholder}
              />
            </FormField>
          </div>

          {/* STEP 9 */}
          <FormField
            label="Language"
            error={errors.preferredLanguages?.message}
          >
            <LanguageSelect
              field={{
                name: "preferredLanguages",
                value: watch("preferredLanguages"),
              }}
              props={register("preferredLanguages")}
              variant="slim"
              placeholder=""
            />
          </FormField>

          {/* STEP 10 */}
          <FormField
            label="Personal context"
            error={errors.personalContext?.message}
          >
            <Textarea
              {...register("personalContext")}
              className="focus:ring-0"
              maxLength={personalContextField.maxLength}
              placeholder={personalContextField.placeholder}
            />
          </FormField>
        </form>
      </Card>
    </>
  );
}
